"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/providers/auth-provider";
import { normalizeArrayOptions } from "@/utils/proposal-helpers";

export interface TodoAction {
  id: string;
  type: "relance" | "manual";
  title: string;
  description?: string;
  chargeAffaire?: string;
  chargeAffaireId?: string;
  dueDate: string; // YYYY-MM-DD
  status: "pending" | "done";
  urgency: "overdue" | "today" | "upcoming";
  proposalId?: string;
  proposalRef?: string;
  clientName?: string;
  amount?: number;
  createdAt: string;
}

export type TodoFilter = "all" | "overdue" | "today" | "week";

function tsToDateStr(val: unknown): string {
  if (val == null || val === "" || val === "0") return "";
  const num = Number(val);
  if (!isNaN(num) && num > 946684800) {
    return new Date(num * 1000).toISOString().split("T")[0];
  }
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}/.test(val)) {
    return val.split("T")[0];
  }
  return "";
}

export function useTodoActions() {
  const { client } = useAuth();

  return useQuery({
    queryKey: ["todo-actions"],
    queryFn: async (): Promise<TodoAction[]> => {
      if (!client) return [];

      const today = new Date().toISOString().split("T")[0];
      const todos: TodoAction[] = [];

      // 1. Relances from proposals with date_relance
      const [proposalsResult, eventsResult] = await Promise.allSettled([
        client.getProposals({
          limit: 200,
          sortfield: "t.rowid",
          sortorder: "DESC",
        }),
        client.getAgendaEvents({
          limit: 50,
          sortfield: "t.datep",
          sortorder: "ASC",
          sqlfilters: "(t.percent:<:100)",
        }),
      ]);

      const proposals =
        proposalsResult.status === "fulfilled" ? proposalsResult.value : [];
      const events =
        eventsResult.status === "fulfilled" ? eventsResult.value : [];

      // Closed etat states — skip these for relances
      const closedEtats = ["accepte", "refuse", "abandonne"];

      for (const p of proposals) {
        const ao = normalizeArrayOptions<Record<string, unknown>>(
          p.array_options
        );
        const dateRelance = tsToDateStr(ao.options_date_relance);
        const etat = String(ao.options_etat_devis || "");

        if (!dateRelance) continue;
        if (closedEtats.includes(etat)) continue;

        const urgency: TodoAction["urgency"] =
          dateRelance < today
            ? "overdue"
            : dateRelance === today
              ? "today"
              : "upcoming";

        todos.push({
          id: `relance-${p.id}`,
          type: "relance",
          title: `Relancer ${p.thirdparty?.name || "Client"}`,
          description: `${p.ref} — ${Number(p.total_ht || 0).toLocaleString("fr-FR")}€`,
          dueDate: dateRelance,
          status: "pending",
          urgency,
          proposalId: p.id,
          proposalRef: p.ref,
          clientName: p.thirdparty?.name,
          amount: Number(p.total_ht || 0),
          createdAt: dateRelance,
          chargeAffaireId: (p as unknown as Record<string, unknown>).user_author_id as string | undefined,
        });
      }

      // 2. Manual actions from agenda events
      for (const ev of events) {
        const datep = tsToDateStr(ev.datep);
        const dueDate = datep || today;
        const pct = Number(ev.percentage ?? 0);

        // Skip system auto events
        if (ev.type === "systemauto") continue;

        const urgency: TodoAction["urgency"] =
          dueDate < today
            ? "overdue"
            : dueDate === today
              ? "today"
              : "upcoming";

        todos.push({
          id: `manual-${ev.id}`,
          type: "manual",
          title: String(ev.label || "Action"),
          description: String(ev.note_private || ev.note || ""),
          chargeAffaireId: ev.userownerid ? String(ev.userownerid) : undefined,
          dueDate,
          status: pct >= 100 ? "done" : "pending",
          urgency,
          proposalId: ev.elementtype === "propal" && ev.elementid ? String(ev.elementid) : undefined,
          createdAt: tsToDateStr(ev.datec) || today,
        });
      }

      // Sort: overdue first, then today, then upcoming — within each group by dueDate ASC
      const urgencyOrder = { overdue: 0, today: 1, upcoming: 2 };
      return todos.sort((a, b) => {
        if (a.status !== b.status) return a.status === "pending" ? -1 : 1;
        const uA = urgencyOrder[a.urgency] ?? 2;
        const uB = urgencyOrder[b.urgency] ?? 2;
        if (uA !== uB) return uA - uB;
        return a.dueDate.localeCompare(b.dueDate);
      });
    },
    enabled: !!client,
    staleTime: 60 * 1000,
  });
}

export function useCompleteTodo() {
  const { client } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (todo: TodoAction) => {
      if (!client) throw new Error("Non authentifié");

      if (todo.type === "relance" && todo.proposalId) {
        // Repousser la date de relance de +7 jours
        const newDate = new Date();
        newDate.setDate(newDate.getDate() + 7);
        const ts = Math.floor(newDate.getTime() / 1000);
        await client.updateProposal(todo.proposalId, {
          array_options: { options_date_relance: ts },
        } as Record<string, unknown>);
      } else if (todo.type === "manual") {
        const eventId = todo.id.replace("manual-", "");
        await client.updateAgendaEvent(eventId, { percentage: 100 });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["todo-actions"] });
      queryClient.invalidateQueries({ queryKey: ["devis-relance"] });
      queryClient.invalidateQueries({ queryKey: ["proposals"] });
      toast.success("Action terminée");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Erreur");
    },
  });
}

export function useCreateManualAction() {
  const { client, user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      title: string;
      dueDate: string;
      notes?: string;
      proposalId?: string;
      socid?: string;
    }) => {
      if (!client) throw new Error("Non authentifié");

      const datep = Math.floor(
        new Date(data.dueDate + "T09:00:00").getTime() / 1000
      );

      await client.createAgendaEvent({
        label: data.title,
        type_code: "AC_OTH",
        datep,
        note: data.notes,
        fk_element: data.proposalId ? Number(data.proposalId) : undefined,
        elementtype: data.proposalId ? "propal" : undefined,
        socid: data.socid ? Number(data.socid) : undefined,
        percentage: 0,
        userownerid: user?.id ? Number(user.id) : 1,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["todo-actions"] });
      toast.success("Action ajoutée");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Erreur lors de la création");
    },
  });
}

export function groupTodos(todos: TodoAction[]) {
  const now = new Date().toISOString().split("T")[0];
  const weekEnd = new Date();
  weekEnd.setDate(weekEnd.getDate() + 7);
  const weekEndStr = weekEnd.toISOString().split("T")[0];

  return {
    overdue: todos.filter(
      (t) => t.status === "pending" && t.dueDate < now
    ),
    today: todos.filter(
      (t) => t.status === "pending" && t.dueDate === now
    ),
    thisWeek: todos.filter(
      (t) =>
        t.status === "pending" && t.dueDate > now && t.dueDate <= weekEndStr
    ),
    later: todos.filter(
      (t) => t.status === "pending" && t.dueDate > weekEndStr
    ),
    done: todos.filter((t) => t.status === "done").slice(0, 5),
  };
}
